import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { IntegrationField } from '@/lib/integrationTypes/integrationTypes';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Control, ControllerRenderProps, FieldValues, UseFormReturn, useFieldArray } from 'react-hook-form';
import UriInput from './UriInput';
import { cn } from '@/lib/utils';

export interface DataDrivenFieldProps {
  integrationField: IntegrationField;
  form: UseFormReturn<FieldValues>;
  routeNamespaceId?: string;
  tenantId?: string;
}

export default function DataDrivenField(props: DataDrivenFieldProps) {
  const field = props.integrationField;
  const control = props.form.control as Control<FieldValues>;
  const baseType = field.dataType.replace(/\[\]$/, '');
  const isArray = /\[\]$/.test(field.dataType);

  return isArray ? (
    <ArrayField
      field={field}
      control={control}
      baseType={baseType}
      routeNamespaceId={props.routeNamespaceId}
      tenantId={props.tenantId}
    />
  ) : (
    <ScalarField
      field={field}
      control={control}
      baseType={baseType}
      routeNamespaceId={props.routeNamespaceId}
      tenantId={props.tenantId}
    />
  );
}

function validateNumber(value: string): { isValid: boolean; error?: string } {
  const trimmed = value.trim();
  if (trimmed === '') {
    return { isValid: true };
  }
  const num = parseFloat(trimmed);
  if (isNaN(num)) {
    return { isValid: false, error: 'Please enter a valid number' };
  }
  // Check for invalid formats like "123abc" - parseFloat would return 123
  if (!/^-?\d*\.?\d+$/.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid number' };
  }
  return { isValid: true };
}

function NumberInput({
  field,
  onValidationError,
}: {
  field: ControllerRenderProps<FieldValues, string>;
  onValidationError?: (error: string | undefined) => void;
}) {
  const [isInvalid, setIsInvalid] = useState(false);

  const validate = (value: string) => {
    const { isValid, error } = validateNumber(value);
    setIsInvalid(!isValid);
    onValidationError?.(error);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    field.onChange(value);
    validate(value);
  };

  const handleBlur = () => {
    const value = typeof field.value === 'string' ? field.value : String(field.value ?? '');
    validate(value);
    field.onBlur();
  };

  return (
    <div className="relative flex-1">
      <Input
        value={field.value ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder=""
        type="text"
        inputMode="decimal"
        autoComplete="off"
        className={cn(
          'pr-9',
          isInvalid && 'border-orange-500 focus-visible:ring-orange-500'
        )}
      />
      {isInvalid && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <AlertCircle className="h-4 w-4 text-orange-500" />
        </div>
      )}
    </div>
  );
}

function InputControl({
  baseType,
  field,
  inputField,
  routeNamespaceId,
  tenantId,
  onValidationError,
}: {
  baseType: string;
  field: IntegrationField;
  inputField: ControllerRenderProps<FieldValues, string>;
  routeNamespaceId?: string;
  tenantId?: string;
  onValidationError?: (error: string | undefined) => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const optionValues = field.options?.filter((option): option is string => Boolean(option?.length));

  // For 'select' type with options, render a dropdown (but not for 'uri' - it uses options differently)
  if (optionValues && optionValues.length > 0 && baseType !== 'uri') {
    const currentValue =
      typeof inputField.value === 'string' && inputField.value.length > 0
        ? inputField.value
        : undefined;

    return (
      <Select value={currentValue} onValueChange={(value) => inputField.onChange(value)}>
        <SelectTrigger>
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {optionValues.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (baseType === 'number') {
    return (
      <NumberInput field={inputField} onValidationError={onValidationError} />
    );
  } else if (baseType === 'boolean') {
    return (
      <div className="flex-1 px-4">
        <Switch checked={Boolean(inputField.value)} onCheckedChange={inputField.onChange} />
      </div>
    );
  } else if (baseType === 'password') {
    return (
      <div className="relative flex-1">
        <Input
          {...inputField}
          type={showPassword ? 'text' : 'password'}
          placeholder=""
          autoComplete="new-password"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
        >
          {showPassword ? (
            <img src="/icon/eye-off.svg" className="h-4 w-4" alt="Hide password" />
          ) : (
            <img src="/icon/eye.svg" className="h-4 w-4" alt="Show password" />
          )}
        </Button>
      </div>
    );
  } else if (baseType === 'uri') {
    // For URI type, options contain allowed protocols (if any)
    const allowedProtocols = field.options?.filter(
      (option): option is string => Boolean(option?.length)
    );
    return (
      <UriInput
        field={inputField}
        routeNamespaceId={routeNamespaceId}
        tenantId={tenantId}
        allowedProtocols={allowedProtocols && allowedProtocols.length > 0 ? allowedProtocols : undefined}
        onValidationError={onValidationError}
      />
    );
  } else {
    return <Input {...inputField} placeholder="" autoComplete="off" />;
  }
}

function ArrayField({
  field,
  control,
  baseType,
  routeNamespaceId,
  tenantId,
}: {
  field: IntegrationField;
  control: Control<FieldValues>;
  baseType: string;
  routeNamespaceId?: string;
  tenantId?: string;
}) {
  const arrayName = field.id as `${string}`;
  const fa = useFieldArray({ control, name: arrayName });
  const [validationErrors, setValidationErrors] = useState<Record<number, string | undefined>>({});
  const additionalStyles = {
    boolean: 'flex flex-row items-center justify-between rounded-lg border p-4',
    password: '',
    string: '',
    number: '',
    select: '',
    uri: '',
  }[baseType];

  function defaultForType() {
    const optionValues = field.options?.filter((o): o is string => Boolean(o?.length));
    if (optionValues && optionValues.length > 0) {
      return optionValues[0];
    }
    if (baseType === 'number') return 0;
    if (baseType === 'boolean') return false;
    if (baseType === 'uri') return '';
    return '';
  }

  useEffect(() => {
    if (fa.fields.length === 0) {
      fa.append(defaultForType() as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fa.fields.length]);

  // Get the first validation error to display next to the label
  const firstError = Object.values(validationErrors).find((e) => e !== undefined);

  return (
    <FormItem className={additionalStyles}>
      <FormLabel className="flex items-center gap-2">
        <span className="text-sm">
          {field.name}
          {field.optional ? (
            <span className="ml-2 text-s text-white/60">(optional)</span>
          ) : (
            <span className="ml-1 text-red-500" aria-hidden="true">*</span>
          )}
        </span>
        {firstError && (
          <span className="flex items-center gap-1 text-sm font-normal text-orange-500">
            {firstError}
          </span>
        )}
      </FormLabel>
      <div className="space-y-2">
        {fa.fields.map((f, index) => (
          <div key={f.id} className="flex items-center gap-2">
            <FormField
              control={control}
              name={`${arrayName}.${index}`}
              render={({ field: inputField }) => (
                <FormControl>
                  <InputControl
                    baseType={baseType}
                    field={field}
                    inputField={inputField}
                    routeNamespaceId={routeNamespaceId}
                    tenantId={tenantId}
                    onValidationError={(error) => {
                      setValidationErrors((prev) => ({ ...prev, [index]: error }));
                    }}
                  />
                </FormControl>
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => {
                fa.remove(index);
                // Clean up validation error for removed item
                setValidationErrors((prev) => {
                  const next = { ...prev };
                  delete next[index];
                  return next;
                });
              }}
              aria-label={`Remove ${field.name}`}
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        ))}
        <div className="flex items-center justify-start">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fa.append(defaultForType() as any)}
            aria-label={`Add ${field.name}`}
          >
            <Plus className="h-4 w-4 text-green-500" />
          </Button>
        </div>
      </div>
      <FormDescription>{field.description}</FormDescription>
      <FormMessage />
    </FormItem>
  );
}

function ScalarField({
  field,
  control,
  baseType,
  routeNamespaceId,
  tenantId,
}: {
  field: IntegrationField;
  control: Control<FieldValues>;
  baseType: string;
  routeNamespaceId?: string;
  tenantId?: string;
}) {
  const [validationError, setValidationError] = useState<string | undefined>();
  const additionalStyles = {
    boolean: 'flex flex-row items-center justify-between rounded-lg border p-4',
    password: '',
    string: '',
    number: '',
    select: '',
    uri: '',
  }[baseType];

  return (
    <FormField
      control={control}
      name={field.id}
      render={({ field: inputField }) => (
        <FormItem className={additionalStyles}>
          <FormLabel className="flex items-center gap-2">
            <span className="text-sm">
              {field.name}
              {field.optional ? (
                <span className="ml-2 text-xs text-white/60">(optional)</span>
              ) : (
                <span className="ml-1 text-red-500" aria-hidden="true">*</span>
              )}
            </span>
            {validationError && (
              <span className="flex items-center gap-1 text-sm font-normal text-orange-500">
                {validationError}
              </span>
            )}
          </FormLabel>
          <FormControl>
            <InputControl
              baseType={baseType}
              field={field}
              inputField={inputField}
              routeNamespaceId={routeNamespaceId}
              tenantId={tenantId}
              onValidationError={setValidationError}
            />
          </FormControl>
          <FormDescription>{field.description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
